/**
 * Authentication State Management (Zustand)
 */

import { create } from "zustand";
import authService from "../services/auth.service";

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: authService.isAuthenticated(),
  isLoading: false,
  error: null,

  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      // Obtain and store JWT tokens.
      await authService.login({ email, password });
      // /auth/token/ returns only tokens, so fetch the user profile now that
      // we are authenticated. Failure here should not block sign-in.
      let user: User | null = null;
      try {
        user = (await authService.getCurrentUser()) as User;
      } catch (_) {
        user = null;
      }
      set({ user, isAuthenticated: true, isLoading: false });
    } catch (error: any) {
      set({
        error:
          error.response?.data?.detail ||
          "Login failed. Please check your credentials.",
        isLoading: false,
        isAuthenticated: false,
        user: null,
      });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await authService.logout();
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    } catch (error) {
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  checkAuth: async () => {
    if (!authService.isAuthenticated()) {
      set({ isAuthenticated: false, user: null });
      return;
    }

    set({ isLoading: true });
    try {
      const user = await authService.getCurrentUser();
      set({ user: user as User, isAuthenticated: true, isLoading: false });
    } catch (error) {
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  clearError: () => set({ error: null }),
}));
